// Internal verification tab for signature-project leads: the signature image,
// the affirmation/consent shown at signing, where the collector stood when it
// was captured, and how far that spot is from the family's home location.
// Modeled on petitionpro's post-sign accountability view. Read-only.
import React, { useEffect, useState } from 'react';
import { Lead } from '../../../types/lead.types';
import { projectService } from '../../../services/project.service';
import { MapPin, PenTool, ShieldCheck, ShieldAlert } from 'lucide-react';

interface LeadSignatureTabProps {
  lead: Lead;
  capturedByName?: string;
}

// Great-circle distance in meters.
const haversineM = (aLat: number, aLng: number, bLat: number, bLng: number): number => {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

const LeadSignatureTab: React.FC<LeadSignatureTabProps> = ({ lead, capturedByName }) => {
  const [attestation, setAttestation] = useState<string>('');
  const [consentText, setConsentText] = useState<string>('');

  // The affirmation/consent wording lives on the project (current wording —
  // shown for reference alongside the captured record).
  useEffect(() => {
    let active = true;
    if (!lead.projectId) return;
    projectService.getProject(lead.projectId).then((p) => {
      if (!active || !p) return;
      const s = (p.settings as any) || {};
      // Business signers saw the business variant (falls back to the household wording).
      setAttestation(lead.isBusiness ? (s.businessSignatureAttestation || s.signatureAttestation || '') : (s.signatureAttestation || ''));
      setConsentText((p.settings as any)?.consentText || '');
    }).catch(() => {});
    return () => { active = false; };
  }, [lead.projectId, lead.isBusiness]);

  const sig = lead.signatureLocation;
  const home = lead.gpsLocation;
  const signedAt = (lead.signatureCapturedAt as any)?.toDate?.() || null;
  const distanceM = sig && home
    ? haversineM(sig.latitude, sig.longitude, home.latitude, home.longitude)
    : null;

  const proximity = distanceM == null
    ? { label: 'Proximity unknown — a location was not recorded', tone: 'bg-gray-100 text-gray-600', Icon: MapPin }
    : distanceM <= 75
      ? { label: `At the home — signed ${Math.round(distanceM)} m from the address location`, tone: 'bg-emerald-100 text-emerald-800', Icon: ShieldCheck }
      : distanceM <= 250
        ? { label: `Nearby — signed ${Math.round(distanceM)} m from the address location`, tone: 'bg-amber-100 text-amber-800', Icon: ShieldCheck }
        : { label: `Away from home — signed ${(distanceM / 1000).toFixed(2)} km from the address location`, tone: 'bg-red-100 text-red-800', Icon: ShieldAlert };

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
  const mapUrl = apiKey && (sig || home)
    ? `https://maps.googleapis.com/maps/api/staticmap?size=640x300&maptype=roadmap${
        home ? `&markers=color:0x4F46E5%7Clabel:H%7C${home.latitude},${home.longitude}` : ''
      }${
        sig ? `&markers=color:0x059669%7Clabel:S%7C${sig.latitude},${sig.longitude}` : ''
      }&key=${apiKey}`
    : null;

  if (!lead.signature) {
    return <p className="text-sm text-gray-500">No signature on this lead.</p>;
  }

  return (
    <div className="space-y-5">
      {/* Signature + affirmation */}
      <div className="border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-3">
          <PenTool className="h-4 w-4 text-indigo-600" /> Signature of interest
        </h3>
        <img
          src={lead.signature}
          alt="Signature"
          className="h-24 w-64 object-contain bg-white border border-gray-200 rounded"
        />
        <div className="mt-3 text-sm text-gray-600 space-y-1">
          {signedAt && <p><span className="font-medium text-gray-700">Signed:</span> {signedAt.toLocaleString()}</p>}
          <p><span className="font-medium text-gray-700">Collector:</span> {lead.capturedByName || capturedByName || '—'}</p>
          {lead.howHeard && <p><span className="font-medium text-gray-700">Heard via:</span> {lead.howHeard}</p>}
          {lead.communitySupporter && (
            <p className="inline-block bg-indigo-50 text-indigo-700 text-xs font-medium px-2 py-0.5 rounded-full">🤝 Community supporter — no eligible child</p>
          )}
          {lead.isBusiness && (
            <p className="inline-block bg-amber-50 text-amber-700 text-xs font-medium px-2 py-0.5 rounded-full">🏢 Business{lead.businessName ? ` — ${lead.businessName}` : ''}{lead.businessRole ? ` · signer: ${lead.businessRole}` : ''}{lead.businessAddress ? ` · ${lead.businessAddress}` : ''}</p>
          )}
        </div>
        {attestation && (
          <p className="mt-3 text-xs text-gray-500 border-l-2 border-indigo-200 pl-2">
            <span className="font-medium text-gray-600">Affirmation shown at signing:</span> “{attestation}”
          </p>
        )}
      </div>

      {/* Consent */}
      <div className={`rounded-lg p-3 text-sm ${lead.consentToContact ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`}>
        <span className="font-semibold">{lead.consentToContact ? '✓ Consented to follow-up contact' : 'No contact consent given'}</span>
        {lead.consentToContact && consentText && (
          <p className="text-xs mt-1 text-emerald-700">“{consentText}”</p>
        )}
      </div>

      {/* Capture location + proximity */}
      <div className="border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-2">
          <MapPin className="h-4 w-4 text-indigo-600" /> Where it was captured
        </h3>
        <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${proximity.tone}`}>
          <proximity.Icon className="h-3.5 w-3.5" /> {proximity.label}
        </span>
        <div className="mt-3 text-xs text-gray-500 space-y-0.5">
          <p><span className="font-medium text-gray-600">Collector GPS at signing (S):</span> {sig ? `${sig.latitude.toFixed(6)}, ${sig.longitude.toFixed(6)}${sig.accuracy != null ? ` (±${Math.round(sig.accuracy)} m)` : ''}` : 'not recorded'}</p>
          <p><span className="font-medium text-gray-600">Home location (H):</span> {home ? `${home.latitude.toFixed(6)}, ${home.longitude.toFixed(6)}` : 'not recorded'} — {[lead.address, lead.city].filter(Boolean).join(', ')}</p>
        </div>
        {mapUrl && (
          <img src={mapUrl} alt="Capture location map" className="mt-3 rounded-lg border border-gray-200 w-full max-w-xl" />
        )}
      </div>
    </div>
  );
};

export default LeadSignatureTab;
